import { useState } from "react";
import { Mail, MessageSquare, ExternalLink, Copy, Check } from "lucide-react";

/**
 * Contact page. Boardroom is a solo-founder operation — no support desk
 * behind a Zendesk portal. This page tells users exactly how to reach the
 * humans building this and sets honest response expectations.
 */
const CONTACT_EMAIL = "hello@foundtheroom.com";
const SUPPORT_EMAIL = "support@foundtheroom.com";

export default function Contact() {
  const [copied, setCopied] = useState(null);

  const copy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(val);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // best-effort; some browsers block programmatic clipboard on http
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-24" data-testid="contact-page">
      <p className="label-eyebrow mt-2 text-[#c89f45]">CONTACT · WE ACTUALLY READ THESE</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
        Get in touch.
      </h1>
      <p className="text-[#bbb] text-base sm:text-lg mt-6 leading-relaxed">
        Boardroom is a small operation. There's no ticket queue, no chatbot —
        just a real inbox monitored daily. Feature requests, bug reports,
        payment issues, or just wanting to say the app helped: send it over.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-10">
        <ContactCard
          Icon={MessageSquare}
          title="General & feedback"
          email={CONTACT_EMAIL}
          note="Product feedback, partnership ideas, press."
          copied={copied === CONTACT_EMAIL}
          onCopy={() => copy(CONTACT_EMAIL)}
          testId="contact-general"
        />
        <ContactCard
          Icon={Mail}
          title="Support & billing"
          email={SUPPORT_EMAIL}
          note="Account issues, refunds, integration bugs. ~24-hour response."
          copied={copied === SUPPORT_EMAIL}
          onCopy={() => copy(SUPPORT_EMAIL)}
          testId="contact-support"
        />
      </div>

      <div className="surface rounded-md p-5 mt-10">
        <p className="label-eyebrow text-[#c89f45]">WHAT TO INCLUDE</p>
        <ul className="mt-3 space-y-2 text-sm text-[#bbb]">
          <li>· Your account email (if you're already signed up)</li>
          <li>· What you were trying to do, and what happened instead</li>
          <li>· A screenshot or a short screen recording if it's a bug</li>
          <li>· The browser + device you're on (helps us reproduce faster)</li>
        </ul>
      </div>

      <p className="text-xs text-[#8a8a8a] mt-10 font-mono">
        Prefer social? Boardroom lives on X /{" "}
        <a
          href="https://twitter.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#c89f45] hover:underline inline-flex items-center gap-1"
          data-testid="contact-x-link"
        >
          @foundtheroom <ExternalLink size={10} />
        </a>
        . Tag us in your wins.
      </p>
    </div>
  );
}

function ContactCard({ Icon, title, email, note, copied, onCopy, testId }) {
  return (
    <div className="surface rounded-md p-5 flex flex-col" data-testid={testId}>
      <Icon size={18} className="text-[#c89f45]" />
      <p className="font-serif text-lg mt-3">{title}</p>
      <a
        href={`mailto:${email}`}
        className="text-[#c89f45] font-mono text-sm mt-3 hover:underline break-all"
      >
        {email}
      </a>
      <p className="text-xs text-[#888] mt-2 leading-relaxed">{note}</p>
      <button
        type="button"
        onClick={onCopy}
        className="mt-3 self-start min-h-[44px] px-1 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#8a8a8a] hover:text-[#c89f45] transition-colors"
        data-testid={`${testId}-copy`}
      >
        {copied ? (
          <>
            <Check size={12} className="text-[#c89f45]" /> Copied
          </>
        ) : (
          <>
            <Copy size={12} /> Copy email
          </>
        )}
      </button>
    </div>
  );
}
